<template>
	<div class="news-list">
		<NewsSidebar class="news-list__sidebar" />
		<div class="news-list__main">
			<h1 v-if="category" class="news-list__category-title">{{ categoryName }}</h1>
			<Transition name="fade" mode="out-in">
				<div v-if="loader" class="news-list__loader">
					<v-progress-circular aria-label="loading" color="black" :size="48"></v-progress-circular>
				</div>
				<div v-else-if="isEmpty" class="news-list__empty">
					{{ $t('pages.news.no_results') }}
				</div>
				<div v-else class="news-list__elements">
					<NewsCard v-for="post in fetchedNews" :key="post.id" :post="post" />
				</div>
			</Transition>

			<NewsPagination v-if="!isEmpty" :currentPage="currentPage" :maxPage="maxPage" :createURL="createURL" />
		</div>
	</div>
</template>

<script setup lang="ts">
const $t = useI18n().t
const newsStore = useNewsStore()
const i18nService = usei18nService()
const localeLang = i18nService.getLocaleLang()
const localePath = useLocalePath()

const pageContext = newsStore.state.pageContext
const category = computed(() => (pageContext?.type === 'category' ? pageContext.category : null))
const categorySlug = pageContext?.type === 'category' ? pageContext.categorySlug : null
const categoryName = computed(() => (category.value ? category.value.name : ''))

const fetcher: TNewsListingFetcher = (page, perPage) => {
	return categorySlug
		? ApiService.fetchNewsByCategorySlug(categorySlug, page, perPage, '', localeLang)
		: ApiService.fetchNews(page, perPage, '', localeLang)
}

const { fetchedNews, loader, currentPage, maxPage, fetchNews } = useNewsListing(fetcher, pageContext?.page ?? 1, 12)

const isEmpty = ref(false)

const createURL = (page: number) => {
	const newsTitle = categorySlug ? (page <= 1 ? [categorySlug] : [categorySlug, page.toString()]) : [page.toString()]

	if (!categorySlug && page <= 1) return localePath('blog')
	return localePath({ name: 'blog-newsTitle', params: { newsTitle } })
}

await fetchNews().then(() => {
	if (!fetchedNews.value || fetchedNews.value.length === 0) {
		// First page with no results is a legitimately empty listing/category — show an empty
		// state. Any later page being empty means the URL points past the last page, which
		// stays a 404.
		if (currentPage.value > 1) {
			throw createError({ statusCode: 404 })
		}
		isEmpty.value = true
	}
})

const requestURL = useRequestURL()

const paginationLinks = computed(() => {
	const links: { rel: string; href: string }[] = []
	if (currentPage.value > 1) links.push({ rel: 'prev', href: requestURL.origin + createURL(currentPage.value - 1) })
	if (currentPage.value < maxPage.value) links.push({ rel: 'next', href: requestURL.origin + createURL(currentPage.value + 1) })
	return links
})

useHead(() => ({ link: paginationLinks.value }))
</script>

<style lang="scss" scoped src="./PageList.scss"></style>
